import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

export enum PasswordStrength {
  Weak = 'weak',
  Medium = 'medium',
  Strong = 'strong',
}

type PasswordStrengthProps = {
  password: string;
  onPasswordStrengthChange?: (strength: PasswordStrength) => void;
};

const PasswordStrengthText = styled.strong<{ strength: PasswordStrength }>`
  color: ${({ strength }) => {
    switch (strength) {
      case PasswordStrength.Weak:
        return 'red';
      case PasswordStrength.Medium:
        return 'orange';
      case PasswordStrength.Strong:
        return 'green';
      default:
        return 'red';
    }
  }};
  flex-shrink: 1;
  position: relative;
  top: -10px;
  padding: 0 3px;
`;

const PasswordStrengthBarContainer = styled.div`
  display: flex;
  margin-top: 10px;
`;

const BarWrap = styled.div`
  width: 100%;
  flex-grow: 2;
`;

const PasswordStrengthBar = styled.div<{ width: number; strength: PasswordStrength }>`
  background-color: ${({ strength }) => {
    switch (strength) {
      case PasswordStrength.Weak:
        return 'red';
      case PasswordStrength.Medium:
        return 'orange';
      case PasswordStrength.Strong:
        return 'green';
      default:
        return 'red';
    }
  }};
  height: 5px;
  width: ${({ width }) => width}%;
`;

const List = styled.ul(({ theme }) => {
  return `
            font-size: ${theme.type.size.small};
            list-style-type: disc;
            padding-left: 15px;
        `;
});

const PasswordStrengthIndicator: React.FC<PasswordStrengthProps> = ({ password, onPasswordStrengthChange }) => {
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>(PasswordStrength.Weak);
  const [passwordConditions, setPasswordConditions] = useState<Array<{ text: string; fulfilled: boolean }>>([]);
  const [passwordStrengthBarWidth, setPasswordStrengthBarWidth] = useState<number>(0);

  const getPasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) {
      strength++;
    }
    if (password.match(/[a-z]/)) {
      strength++;
    }
    if (password.match(/[A-Z]/)) {
      strength++;
    }
    if (password.match(/[0-9]/)) {
      strength++;
    }
    if (password.match(/[^a-zA-Z0-9]/)) {
      strength++;
    }
    switch (strength) {
      case 1:
      case 2:
        return PasswordStrength.Weak;
      case 3:
      case 4:
        return PasswordStrength.Medium;
      case 5:
        return PasswordStrength.Strong;
      default:
        return PasswordStrength.Weak;
    }
  };

  const getPasswordConditions = (password: string) => {
    const conditions = [
      { text: 'At least 8 characters', fulfilled: password.length >= 8 },
      { text: 'At least one lowercase letter', fulfilled: password.match(/[a-z]/) !== null },
      { text: 'At least one uppercase letter', fulfilled: password.match(/[A-Z]/) !== null },
      { text: 'At least one digit', fulfilled: password.match(/[0-9]/) !== null },
      {
        text: 'At least one special character (!, @, #, $, etc.)',
        fulfilled: password.match(/[^a-zA-Z0-9]/) !== null,
      },
    ];
    return conditions;
  };

  const calculatePasswordStrengthBarWidth = (strength: PasswordStrength) => {
    switch (strength) {
      case PasswordStrength.Weak:
        return 25;
      case PasswordStrength.Medium:
        return 50;
      case PasswordStrength.Strong:
        return 100;
      default:
        return 0;
    }
  };

  useEffect(() => {
    const strength = getPasswordStrength(password);
    const conditions = getPasswordConditions(password);
    const barWidth = calculatePasswordStrengthBarWidth(strength);
    setPasswordStrength(strength);
    setPasswordConditions(conditions);
    setPasswordStrengthBarWidth(barWidth);
    if (onPasswordStrengthChange) {
      onPasswordStrengthChange(strength);
    }
  }, [password, onPasswordStrengthChange]);

  return (
    <div>
      <PasswordStrengthBarContainer>
        <BarWrap>
          <PasswordStrengthBar width={passwordStrengthBarWidth} strength={passwordStrength} />
        </BarWrap>

        <PasswordStrengthText strength={passwordStrength}>{passwordStrength}</PasswordStrengthText>
      </PasswordStrengthBarContainer>
      <List>
        {passwordConditions.map((condition, index) => (
          <li key={index} style={{ color: condition.fulfilled ? 'green' : 'inherit' }}>
            {condition.text}
          </li>
        ))}
      </List>
    </div>
  );
};

export default PasswordStrengthIndicator;
